import React, { useRef, useEffect, useState, useMemo, useCallback, ReactNode, ReactElement } from "react";
import { View, Animated, ScrollView, StyleSheet, TouchableOpacity, Text, Platform, BackHandler } from "react-native";
import { Stack, router, useNavigation, usePathname } from "expo-router";
import { Ionicons } from "@expo/vector-icons";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import Constants from "expo-constants";
import { useThemeColor } from "@/hooks/useThemeColor";
import { Fonts, BlueLinenRange, BrandColors } from "@/constants/ThemeColours";
import { useMessaging } from "@/hooks/useMessaging";
import { FeedbackModal } from "@/components/feedback/FeedbackModal";
import { FeedbackIcon } from "@/components/feedback/FeedbackIcon";
import { UploadQueueIndicator } from "@/components/ui/UploadQueueIndicator";
import { getAppConfig } from "@/services/globals";
import {
  isHomeScreen as checkIsHomeScreen,
  hasPreviousRoute,
  navigateBack,
} from "@/services/navigation-history";

type ThemedScreenProps = {
  children: ReactNode;
  title?: string;
  headerImage?: ReactElement | null;
  headerBackgroundColor?: { dark: string; light: string };
  leftIcon?: {
    name: keyof typeof Ionicons.glyphMap;
    onPress: () => void;
    disabled?: boolean;
  } | null;
  rightIcon?: {
    name: keyof typeof Ionicons.glyphMap;
    onPress: () => void;
  } | null;
  /** Optional custom React node to render in the right header area (e.g. ThemedKebabMenu) */
  rightComponent?: ReactNode;
  showMessagesButton?: boolean;
  showFeedbackButton?: boolean;
  showEnvironmentBadge?: boolean;
  loading?: boolean;
  scrollable?: boolean;
  showNavigationTitle?: boolean;
};

export default function ThemedScreen({
  title,
  headerImage,
  headerBackgroundColor,
  children,
  leftIcon,
  rightIcon,
  rightComponent,
  showMessagesButton = true,
  showFeedbackButton = true,
  showEnvironmentBadge = true,
  loading,
  scrollable = true,
  showNavigationTitle,
}: ThemedScreenProps) {
  const [feedbackModalVisible, setFeedbackModalVisible] = useState(false);
  const { unreadCount } = useMessaging();
  const hasUnread = unreadCount > 0;
  const mountTimeRef = useRef(performance.now());
  const loggedRef = useRef(false);

  const navigation = useNavigation();
  const pathname = usePathname();

  const clientEnv = useMemo(() => {
    try {
      return getAppConfig().environ.getName().toUpperCase();
    } catch {
      const extraEnv = Constants.expoConfig?.extra?.env;
      return extraEnv ? String(extraEnv).toUpperCase() : "LOCAL";
    }
  }, []);

  useEffect(() => {
    if (loading === false && !loggedRef.current) {
      const duration = performance.now() - mountTimeRef.current;
      console.info(`[Page Load Metric] Screen "${title || "unnamed"}" loaded in ${duration.toFixed(2)}ms`);
      loggedRef.current = true;
    }
  }, [loading, title]);

  useEffect(() => {
    if (loading === undefined) {
      const duration = performance.now() - mountTimeRef.current;
      console.info(`[Page Load Metric] Screen "${title || "unnamed"}" mounted in ${duration.toFixed(2)}ms`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scrollY = useRef(new Animated.Value(0)).current;
  const insets = useSafeAreaInsets();

  const defaultHeaderBg = { light: BlueLinenRange.wash, dark: "#20293D" };
  const headerBgColor = useThemeColor(
    {
      light: headerBackgroundColor?.light ?? defaultHeaderBg.light,
      dark: headerBackgroundColor?.dark ?? defaultHeaderBg.dark,
    },
    "header"
  );
  const screenBgColor = useThemeColor({}, "background");
  const borderColor = useThemeColor({}, "border");
  const textColor = useThemeColor({}, "headerText");
  const primaryTint = useThemeColor({}, "tint");

  const envBadgeStyle = useMemo(() => {
    return {
      bg: primaryTint,
      color: "#000000",
      border: "rgba(0, 0, 0, 0.18)",
    };
  }, [primaryTint]);

  const handleScroll = Animated.event([{ nativeEvent: { contentOffset: { y: scrollY } } }], { useNativeDriver: false });

  const headerBorderWidth = scrollY.interpolate({
    inputRange: [0, 50],
    outputRange: [0, StyleSheet.hairlineWidth],
    extrapolate: "clamp",
  });

  const shouldShowTitle = showNavigationTitle ?? true;
  const resolvedTitle = shouldShowTitle ? (title ?? "") : "";

  const isHomeScreen = useMemo(() => {
    return checkIsHomeScreen(pathname);
  }, [pathname]);

  const canNavigateBack = useMemo(() => {
    if (!isHomeScreen) return true;
    try {
      if (typeof navigation?.canGoBack === "function" && navigation.canGoBack()) {
        return true;
      }
      const parent = typeof navigation?.getParent === "function" ? navigation.getParent() : undefined;
      if (parent && typeof parent.canGoBack === "function" && parent.canGoBack()) {
        return true;
      }
      if (typeof router.canGoBack === "function" && router.canGoBack()) {
        return true;
      }
      if (hasPreviousRoute()) {
        return true;
      }
      if (Platform.OS === "web" && typeof window !== "undefined") {
        const historyState = window.history?.state;
        if (historyState && typeof historyState.idx === "number" && historyState.idx > 0) {
          return true;
        }
      }
    } catch {
      // safe fallback
    }
    return false;
  }, [isHomeScreen, navigation]);

  const handleDefaultBack = useCallback(() => {
    navigateBack(router, navigation, pathname);
  }, [navigation, pathname]);

  useEffect(() => {
    if (Platform.OS !== "android") return;

    const onHardwareBack = () => {
      if (isHomeScreen && !canNavigateBack) {
        return false;
      }
      handleDefaultBack();
      return true;
    };

    const subscription = BackHandler.addEventListener("hardwareBackPress", onHardwareBack);
    return () => subscription.remove();
  }, [isHomeScreen, canNavigateBack, handleDefaultBack]);

  const resolvedLeftIcon = useMemo(() => {
    if (leftIcon === null) return undefined;
    if (leftIcon !== undefined) return leftIcon;
    if (isHomeScreen) {
      return {
        name: "arrow-back" as const,
        onPress: handleDefaultBack,
        disabled: !canNavigateBack,
      };
    }
    return {
      name: "arrow-back" as const,
      onPress: handleDefaultBack,
      disabled: false,
    };
  }, [leftIcon, isHomeScreen, canNavigateBack, handleDefaultBack]);

  const resolvedRightIcon =
    rightIcon === null
      ? undefined
      : rightIcon !== undefined
      ? rightIcon
      : {
          name: "person-circle-outline" as const,
          onPress: () => {
            router.navigate("/(tabs)/profile");
          },
        };

  const resolvedHeaderImage = headerImage ?? null;

  return (
    <>
      <Stack.Screen
        options={{
          headerShown: true,
          headerTitleAlign: "center",
          headerTitle: resolvedTitle,
          headerTitleStyle: {
            fontFamily: Fonts.serif,
            fontSize: 16,
            fontWeight: "700",
          },
          headerStyle: {
            backgroundColor: headerBgColor,
            ...(Platform.OS === "web"
              ? { height: 48 }
              : { height: (insets?.top || 0) + (Platform.OS === "android" ? 48 : 44) }),
          },
          headerTintColor: textColor,

          headerLeft:
            resolvedLeftIcon || showEnvironmentBadge !== false
              ? () => (
                  <View style={styles.headerLeftContainer}>
                    {resolvedLeftIcon && (
                      <TouchableOpacity
                        onPress={resolvedLeftIcon.disabled ? undefined : resolvedLeftIcon.onPress}
                        disabled={resolvedLeftIcon.disabled}
                        style={[
                          styles.leftIconContainer,
                          resolvedLeftIcon.disabled && { opacity: 0.25 },
                        ]}
                        accessibilityRole="button"
                        accessibilityState={{ disabled: resolvedLeftIcon.disabled }}
                        accessibilityLabel={resolvedLeftIcon.disabled ? "No previous history" : "Go back to previous screen"}
                      >
                        <Ionicons name={resolvedLeftIcon.name} size={24} color={textColor} />
                      </TouchableOpacity>
                    )}
                    {showEnvironmentBadge !== false && (
                      <View
                        style={[
                          styles.envBadge,
                          {
                            backgroundColor: envBadgeStyle.bg,
                            borderColor: envBadgeStyle.border,
                          },
                        ]}
                      >
                        <Text style={[styles.envBadgeText, { color: envBadgeStyle.color }]}>
                          {clientEnv}
                        </Text>
                      </View>
                    )}
                  </View>
                )
              : undefined,

          headerRight: () => (
            <View style={styles.headerRightContainer}>
              <UploadQueueIndicator />
              {showFeedbackButton !== false && (
                <TouchableOpacity
                  onPress={() => setFeedbackModalVisible(true)}
                  style={styles.feedbackButtonContainer}
                  accessibilityRole="button"
                  accessibilityLabel="Send Feedback"
                >
                  <FeedbackIcon size={22} color={textColor} />
                </TouchableOpacity>
              )}

              {showMessagesButton !== false && (
                <TouchableOpacity
                  onPress={() => router.navigate("/(tabs)/messages")}
                  style={styles.messageButtonContainer}
                  accessibilityRole="button"
                  accessibilityLabel={hasUnread ? `Messages, ${unreadCount} unread` : "Messages"}
                >
                  <Ionicons
                    name={hasUnread ? "mail" : "mail-outline"}
                    size={22}
                    color={hasUnread ? BrandColors.sungoldOrange.hex : textColor}
                  />
                  {hasUnread && (
                    <View style={styles.unreadBadge}>
                      <Text style={styles.unreadBadgeText}>
                        {unreadCount > 99 ? "99+" : unreadCount}
                      </Text>
                    </View>
                  )}
                </TouchableOpacity>
              )}

              {rightComponent ? (
                <View style={styles.rightIconContainer}>{rightComponent}</View>
              ) : resolvedRightIcon ? (
                <TouchableOpacity onPress={resolvedRightIcon.onPress} style={styles.rightIconContainer}>
                  <Ionicons name={resolvedRightIcon.name} size={24} color={textColor} />
                </TouchableOpacity>
              ) : null}
            </View>
          ),

          headerBackground: () => (
            <Animated.View
              style={[
                StyleSheet.absoluteFill,
                styles.headerBackground,
                {
                  backgroundColor: headerBgColor,
                  borderBottomColor: borderColor,
                  borderBottomWidth: headerBorderWidth,
                },
              ]}
            >
              {resolvedHeaderImage}
            </Animated.View>
          ),
        }}
      />

      {scrollable ? (
        <ScrollView
          style={[styles.scrollView, { backgroundColor: screenBgColor }]}
          contentContainerStyle={[styles.scrollViewContent, { paddingBottom: insets.bottom + 20 }]}
          onScroll={handleScroll}
          scrollEventThrottle={16}
        >
          <View style={styles.content}>{children}</View>
        </ScrollView>
      ) : (
        <View
          style={[
            styles.nonScrollableContainer,
            { backgroundColor: screenBgColor, paddingBottom: 0 },
          ]}
        >
          <View style={[styles.content, styles.nonScrollableContent]}>{children}</View>
        </View>
      )}

      <FeedbackModal
        visible={feedbackModalVisible}
        onClose={() => setFeedbackModalVisible(false)}
      />
    </>
  );
}

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
  },
  scrollViewContent: {
    flexGrow: 1,
  },
  nonScrollableContainer: {
    flex: 1,
    minHeight: 0,
  },
  nonScrollableContent: {
    minHeight: 0,
  },
  content: {
    flex: 1,
    paddingHorizontal: 8,
    paddingTop: 6,
  },
  headerBackground: {
    borderBottomWidth: 0,
    overflow: "hidden",
  },
  headerLeftContainer: {
    flexDirection: "row",
    alignItems: "center",
    marginLeft: 16,
    gap: 8,
  },
  leftIconContainer: {
    padding: 4,
  },
  envBadge: {
    paddingHorizontal: 7,
    paddingVertical: 2,
    borderRadius: 6,
    borderWidth: 1,
    alignItems: "center",
    justifyContent: "center",
  },
  envBadgeText: {
    fontSize: 10,
    fontWeight: "700",
    letterSpacing: 0.6,
    textTransform: "uppercase",
  },
  headerRightContainer: {
    flexDirection: "row",
    alignItems: "center",
    marginRight: 16,
    gap: 10,
    overflow: "visible",
  },
  feedbackButtonContainer: {
    padding: 4,
    justifyContent: "center",
    alignItems: "center",
  },
  messageButtonContainer: {
    paddingVertical: 4,
    paddingLeft: 4,
    paddingRight: 8,
    position: "relative",
    justifyContent: "center",
    alignItems: "center",
    overflow: "visible",
  },
  unreadBadge: {
    position: "absolute",
    top: -2,
    right: 0,
    backgroundColor: BrandColors.sungoldOrange.hex,
    borderRadius: 9,
    minWidth: 18,
    height: 18,
    alignItems: "center",
    justifyContent: "center",
    paddingHorizontal: 4,
  },
  unreadBadgeText: {
    color: "#FFFFFF",
    fontSize: 10,
    fontWeight: "700",
    textAlign: "center",
    lineHeight: Platform.OS === "android" ? 12 : undefined,
    includeFontPadding: false,
  },
  rightIconContainer: {
    padding: 4,
  },
});
